import { ActionButton, Alert, Button, Dialog, Field, Select } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { CalendarIcon, CopyIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { m } from "~/paraglide/messages";

import { compareAirHandlerUnitNames } from "../helpers/air-handler-unit.helper";

interface Place {
  id: string;
  name: string;
  parentName?: string;
  /** Domain ids the place stands for (e.g. every unit of an AHU group). Defaults to `[id]`. */
  parentIds?: string[];
}

interface CopyExceptionalPlanningDialogProps extends Dialog.RootProps {
  source: Place;
  places: Place[];
  onCopy?: (targetIds: string[]) => void;
  disabled?: boolean;
}

type CopyFormValues = {
  targetPlaces: string[];
};

const defaultValues: CopyFormValues = { targetPlaces: [] };

export const CopyExceptionalPlanningDialog: React.FC<CopyExceptionalPlanningDialogProps> = ({
  source,
  places,
  onCopy,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen = false,
  disabled,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });

  const form = useAppForm({
    defaultValues,
    onSubmit: ({ value }) => {
      const targetIds = value.targetPlaces.flatMap(
        placeId => places.find(place => place.id === placeId)?.parentIds ?? [placeId],
      );
      onCopy?.(targetIds);
      handleOpenChange(false);
    },
  });

  const placesOptions = places
    .filter(p => p.id !== source.id)
    .map(({ id, name }) => ({ value: id, name }))
    .sort((a, b) => compareAirHandlerUnitNames(a.name, b.name));

  const handleOpenChange = (nextOpen: boolean) => {
    form.reset(defaultValues);
    setOpen(nextOpen);
  };

  return (
    <Dialog.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <Button disabled={disabled}>
          <CopyIcon />
        </Button>
      </Dialog.Trigger>
      <Dialog.Content title={m.air_handler_unit_exceptional_planning_copy_title()} icon={<CalendarIcon />}>
        <form
          className="w-110 gap-10 flex flex-col items-center text-base"
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <div className="gap-2.5 flex w-full flex-col">
              <form.AppField
                name="targetPlaces"
                validators={{
                  onSubmit: ({ value }) =>
                    value.length === 0 ? m.air_handler_unit_exceptional_planning_copy_required() : undefined,
                }}
              >
                {field => (
                  <Field.Root>
                    <Field.Label>
                      {m.air_handler_unit_exceptional_planning_copy_label({ name: source.name })}
                    </Field.Label>
                    <Select.Root multiple value={field.state.value} onValueChange={field.handleChange}>
                      <Select.Trigger className="w-full" onBlur={field.handleBlur}>
                        <Select.Value placeholder={m.air_handler_unit_exceptional_planning_copy_placeholder()} />
                      </Select.Trigger>
                      <Select.Content>
                        {placesOptions.length > 10 && <Select.Input placeholder={m.search()} />}
                        <Select.Viewport>
                          {placesOptions.map(({ value, name }) => (
                            <Select.Item key={value} value={value}>
                              {name}
                              <Select.ItemText>{name}</Select.ItemText>
                            </Select.Item>
                          ))}
                          <Select.Empty>{m.no_result()}</Select.Empty>
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Root>
                  </Field.Root>
                )}
              </form.AppField>
              <Alert severity="warning">{m.air_handler_unit_exceptional_planning_copy_warning()}</Alert>
            </div>
            <div className="gap-5 flex items-center">
              <Dialog.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Dialog.Close>
              <form.SubmitButton label={m.copy_plan()} />
            </div>
          </form.AppForm>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export type { CopyExceptionalPlanningDialogProps };
